/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { useContext } from "react";
import { Row, Col } from "react-awesome-styled-grid";
import { Cell } from "react-table";
import { Box, Text } from "@nordcloud/gnui";
import { FlexContainer, Table } from "~/components";
import { APPlanAdd } from "~/components/Applications/ApplicationDetails/APPlanAdd";
import { APPlanRemove } from "~/components/Applications/ApplicationDetails/APPlanRemove";
import { InlineEditText } from "~/components/InlineEdit/InlineEditText";
import { DetailRow } from "~/components/Utils/DetailRow";
import { APPlan } from "~/models";
import { ApplicationViewCtx } from "~/views/customer/applications/ApplicationViewCtx";

function APPlans() {
  const { application, updateApplication, canEdit } = useContext(
    ApplicationViewCtx
  );
  const plans = application.apPlans;

  const addPlan = React.useCallback(
    (newPlan: APPlan) => {
      return updateApplication({
        apPlans: [...plans, newPlan],
      });
    },
    [plans, updateApplication]
  );

  const removePlan = React.useCallback(
    (idx) => {
      return updateApplication({
        apPlans: plans.filter((_, i) => i !== idx),
      });
    },
    [plans, updateApplication]
  );

  const columns = React.useMemo(
    () => [
      {
        Header: "Customer name",
        accessor: "customerName",
      },
      {
        Header: "Plan",
        accessor: "plan",
        Cell: ({ value }: Cell<APPlan>) => (
          <a href={value} target="_blank" rel="noopener noreferrer">
            {value}
          </a>
        ),
      },
      {
        Header: "Environments",
        accessor: "environments",
        Cell: ({ value }: Cell<APPlan>) => value?.join(", ") || "–",
      },
      {
        Header: "Schedule",
        accessor: "cron",
        Cell: ({ value }: Cell<APPlan>) => value || "–",
      },
      {
        Header: "Actions",
        accessor: "actions",
        disableSortBy: true,
        Cell: ({ row }: Cell<APPlan>) => {
          return (
            canEdit && (
              <APPlanRemove
                removeCallback={removePlan}
                plan={row?.original}
                idx={row.index}
              />
            )
          );
        },
      },
    ],
    [canEdit, removePlan]
  );

  const environments = application.environments.map((env) => env.name);

  return (
    <Box spacing="spacing04">
      <Row>
        <Col lg={12}>
          <FlexContainer>
            <Text weight="medium">AutoPatcher plans</Text>
            <APPlanAdd environments={environments} addPlan={addPlan} />
          </FlexContainer>
          <DetailRow title="Manual Patching">
            <InlineEditText
              name="manualPatching"
              defaultValue={application.manualPatching}
              onSubmit={(val) => updateApplication({ manualPatching: val })}
              canEdit={canEdit}
            />
          </DetailRow>
          <Table columns={columns} data={plans} mb="0" />
        </Col>
      </Row>
    </Box>
  );
}

export { APPlans };
